import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Box, Text } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type FeaturePanel = {
  title: string;
  items: readonly string[];
};

type FeaturePanelsSectionProps = {
  width: number;
  panels: readonly FeaturePanel[];
};

const underlineColors = ['primary', 'purple', 'pink'] as const;
const panelIcons = ['/logo.png', '/logo-purple.png', '/logo-pink.png'] as const;
const OR_BREAKING = '(or breaking)';

const renderItemText = (item: string): ReactNode => {
  const orBreakingIndex = item.indexOf(OR_BREAKING);
  if (orBreakingIndex === -1) return item;
  const before = item.slice(0, orBreakingIndex);
  const after = item.slice(orBreakingIndex + OR_BREAKING.length);
  return (
    <>
      {before}(
      <Box component="em" sx={{ fontStyle: 'italic' }}>
        or breaking
      </Box>
      ){after}
    </>
  );
};

export const FeaturePanelsSection = ({
  width,
  panels,
}: FeaturePanelsSectionProps): ReactElement => (
  <Box component="section" sx={{ px: { xs: s('none'), sm: s('none') } }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        rowGap: { xs: s('xl'), md: s('md') },
        columnGap: { xs: s('xl'), md: s('3xl') },
        maxWidth: { xs: width, md: `calc(${width}px + ${s('3xl')})` },
        mx: 'auto',
      }}
    >
      {panels.map((panel, index) => {
        const underlineColor = underlineColors[index % underlineColors.length];
        return (
          <Box
            key={panel.title}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: r('card'),
              paddingY: s('2xl'),
              paddingX: s('xl'),
              paddingTop: s('xl'),
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ mb: s('md') }}>
              <Text
                role="sectionTitle"
                component="h2"
                transform="none"
                sx={(theme) => ({
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: s('sm'),
                  paddingBottom: s('md'),
                  borderBottom: `${s('xs')} solid ${theme.app.colors[underlineColor]}`,
                })}
              >
                <Box
                  component="img"
                  src={panelIcons[index % panelIcons.length]}
                  alt=""
                  aria-hidden
                  sx={(theme) => ({
                    width: theme.app.iconSizes.lg,
                    height: theme.app.iconSizes.lg,
                    display: 'block',
                    flexShrink: 0,
                    transform: 'translateY(3px)',
                  })}
                />
                <Box component="span">{panel.title}</Box>
              </Text>
            </Box>
            <Stack
              component="ul"
              gap="sm"
              sx={{ pl: s('lg'), m: s('none'), color: 'text.secondary' }}
            >
              {panel.items.map((item) => (
                <li key={item}>
                  <Text role="bodyMuted" component="span">
                    {renderItemText(item)}
                  </Text>
                </li>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  </Box>
);
